/**
 * The price list, public: every plan with what it includes and Stripe's
 * amount, the credit packs, and what a person can do from where they are:
 * sign in, start the trial, or buy. Buying opens Stripe's embedded Checkout
 * in place (or the hosted page, when the API says so).
 */
import { useEffect, useState } from 'react';
import { ApiError, type Pricing, type PricingPlan, type WebApi } from '../api.js';
import type { LoadStripe } from '../stripe.js';
import { Button, Icon, Note } from '../ui/index.js';
import { CheckoutPanel } from './Checkout.js';

export type PricingViewer = { kind: 'signed-out' } | { kind: 'no-org' } | { kind: 'member'; role: 'admin' | 'editor' | 'viewer'; plan: string };

export function money(amount: number | null | undefined, currency: string | undefined): string | null {
  if (amount === null || amount === undefined || !currency) return null;
  const whole = amount % 100 === 0;
  return new Intl.NumberFormat('en', { style: 'currency', currency: currency.toUpperCase(), minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(amount / 100);
}
const machines = (n: number | null) => (n === null ? 'Unlimited machines' : n === 1 ? '1 machine' : `Up to ${n} machines`);

export function PricingPage({ api, viewer, loadStripe, openUrl, onSignIn, onStartTrial }: { api: WebApi; viewer: PricingViewer; loadStripe: LoadStripe; openUrl: (url: string) => void; onSignIn: () => void; onStartTrial: () => Promise<void> }) {
  const [pricing, setPricing] = useState<Pricing | null>(null); const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); const [checkout, setCheckout] = useState<{ clientSecret: string; publishableKey: string; what: string } | null>(null);
  useEffect(() => { let on = true; api.pricing().then((p) => { if (on) setPricing(p); }, (e) => { if (on) setError(e instanceof ApiError ? e.message : String(e)); }); return () => { on = false; }; }, [api]);
  const buy = async (body: { plan: string } | { credits: number }, what: string) => {
    setError(null); setBusy(what);
    try {
      const r = await api.billing.checkout(body);
      if (r.mode === 'hosted' && r.url) { openUrl(r.url); return; }
      if (r.clientSecret && pricing?.publishableKey) setCheckout({ clientSecret: r.clientSecret, publishableKey: pricing.publishableKey, what });
      else setError('Checkout could not start.');
    } catch (e) { setError(e instanceof ApiError ? e.message : String(e)); }
    finally { setBusy(null); }
  };
  const startTrial = async () => { setError(null); setBusy('trial'); try { await onStartTrial(); } catch (e) { setError(e instanceof ApiError ? e.message : String(e)); } finally { setBusy(null); } };
  const cta = (p: PricingPlan) => {
    if (p.id === 'trial') {
      if (viewer.kind === 'signed-out') return <Button variant="primary" onClick={onSignIn}>Sign in to start</Button>;
      if (viewer.kind === 'no-org') return <Button variant="primary" busy={busy === 'trial'} disabled={busy !== null} onClick={() => void startTrial()}>Start free trial</Button>;
      return <span className="rv-body rv-muted">{viewer.plan === 'trial' ? 'Your plan' : 'Included at the start'}</span>;
    }
    if (!p.priceId) return <span className="rv-body rv-muted">Talk to us</span>;
    if (viewer.kind === 'signed-out') return <Button onClick={onSignIn}>Sign in to buy</Button>;
    if (viewer.kind === 'no-org') return <span className="rv-body rv-muted">Start the trial first</span>;
    if (viewer.plan === p.id) return <span className="rv-body rv-muted">Your plan</span>;
    if (viewer.role !== 'admin') return <span className="rv-body rv-muted">Ask your admin</span>;
    return <Button variant="primary" busy={busy === p.id} disabled={busy !== null} onClick={() => void buy({ plan: p.id }, p.id)}>Choose {p.name}</Button>;
  };
  return (
    <section className="rv-page rv-pricing">
      <header className="rv-page__head"><h1 className="rv-title">Pricing</h1>{viewer.kind === 'signed-out' ? <Button variant="ghost" onClick={onSignIn}>Sign in</Button> : null}</header>
      {error ? <Note tone="danger">{error}</Note> : null}
      {checkout ? <CheckoutPanel key={checkout.clientSecret} clientSecret={checkout.clientSecret} publishableKey={checkout.publishableKey} loadStripe={loadStripe} title={`Checkout: ${checkout.what}`} onClose={() => setCheckout(null)} /> : null}
      {pricing === null ? <p className="rv-body rv-muted">Loading…</p> : (
        <>
          <ul className="rv-plans" aria-label="Plans">
            {pricing.plans.map((p) => {
              const price = money(p.amount, p.currency);
              return (
                <li key={p.id} className={`rv-plan${viewer.kind === 'member' && viewer.plan === p.id ? ' is-current' : ''}`}>
                  <h2 className="rv-heading">{p.name}</h2>
                  <p className="rv-plan__price">{p.id === 'trial' ? <>Free<span className="rv-plan__per"> for {p.trialDays ?? 14} days</span></> : price ? <>{price}<span className="rv-plan__per"> / {p.interval ?? 'month'}</span></> : <span className="rv-muted">Price on request</span>}</p>
                  <ul className="rv-plan__facts">
                    <li><Icon name="coins" size={14} /> {p.creditsIncluded.toLocaleString('en')} credits a month</li>
                    <li><Icon name="monitor" size={14} /> {machines(p.machineLimit)}</li>
                    <li><Icon name="upload" size={14} /> {p.storageGb >= 1000 ? `${p.storageGb / 1000} TB` : `${p.storageGb} GB`} storage</li>
                    {p.dedicated ? <li><Icon name="sparkles" size={14} /> Own database and server</li> : null}
                  </ul>
                  <div className="rv-plan__cta">{cta(p)}</div>
                </li>);
            })}
          </ul>
          {pricing.packs.length ? (
            <section className="rv-stack" aria-label="Credit packs">
              <h2 className="rv-heading">Credit packs</h2>
              <p className="rv-body rv-muted">Packs never expire while you subscribe.</p>
              <div className="rv-row">
                {pricing.packs.map((k) => {
                  const label = `${k.credits.toLocaleString('en')} credits${money(k.amount, k.currency) ? ` · ${money(k.amount, k.currency)}` : ''}`;
                  return viewer.kind === 'member' && viewer.role === 'admin'
                    ? <Button key={k.credits} icon="coins" busy={busy === `pack-${k.credits}`} disabled={busy !== null} onClick={() => void buy({ credits: k.credits }, `${k.credits.toLocaleString('en')} credits`)}>{label}</Button>
                    : <span key={k.credits} className="rv-chip rv-chip--neutral">{label}</span>;
                })}
              </div>
            </section>) : null}
          {!pricing.configured ? <Note>Buying opens once payments are connected.</Note> : null}
        </>)}
    </section>
  );
}
